import { useEffect, useRef } from "react";
import { cn } from "@shared/lib/utils";

/*
 * §3 signature — "materialized transparency". Odometer roll-down + ONE
 * gold flash on change: color transition only, no glow. Under
 * prefers-reduced-motion the global base.css rule collapses the animation
 * to an instant swap.
 */
const STYLE_ID = "ty-supply-counter-kf";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent =
    "@keyframes ty-digit-roll{0%{transform:translateY(-100%);color:var(--ty-accent)}55%{color:var(--ty-accent)}100%{transform:translateY(0);color:inherit}}";
  document.head.appendChild(style);
}

/**
 * Live remaining-supply counter — oversized Geist Mono, one cell per digit.
 * Shop cluster ONLY (shop / pack detail / Ultra Chase); every other screen
 * keeps the §1.2 standard numeric grammar.
 */
export function SupplyCounter({
  value,
  label,
  className,
}: {
  value: number;
  /** eyebrow above the digits, e.g. "Packs left" */
  label?: string;
  className?: string;
}) {
  const chars = Math.max(0, Math.trunc(value)).toLocaleString("en-US").split("");
  const prev = useRef<string[] | null>(null);
  const old = prev.current;

  useEffect(() => {
    prev.current = chars;
  });

  return (
    <div className={cn("flex flex-col items-start gap-2", className)}>
      {label && <span className="ty-eyebrow text-xs">{label}</span>}
      <span className="sr-only">{Math.max(0, Math.trunc(value)).toLocaleString("en-US")}</span>
      <span
        aria-hidden
        className="flex items-center gap-1 font-mono text-5xl font-medium tabular-nums text-ink sm:text-7xl"
      >
        {chars.map((ch, i) => {
          if (!/\d/.test(ch)) {
            return (
              <span key={`s-${i}`} className="text-ink-mute">
                {ch}
              </span>
            );
          }
          // Align against previous digits from the right so a length change
          // still compares the same decimal position.
          const oldCh = old?.[old.length - (chars.length - i)];
          const changed = old != null && oldCh !== ch;
          return (
            <span
              key={`${i}-${ch}`}
              className="flex h-[1.18em] w-[0.82em] items-center justify-center overflow-hidden rounded-[6px] border border-line bg-white/4"
            >
              <span
                className="block"
                style={changed ? { animation: "ty-digit-roll 0.55s var(--ty-ease) both" } : undefined}
              >
                {ch}
              </span>
            </span>
          );
        })}
      </span>
    </div>
  );
}
